/**
 * ChatPanel - Main coordinator component for the chat interface
 *
 * This is a thin coordinator that composes extracted components and hooks.
 * Sidebars (ControlPanelOverlay, DocumentContextSelector) and their hooks
 * (useChatSettings, useMessageConfig) have been lifted to MainLayout.
 */

import React, { useRef, useCallback, useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { apiService } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
// isTyping state is bridged from ChatInputArea via EventBus (TYPING_STATE_CHANGED)
import { useGlobalLoading } from '../contexts/LoadingContext';
import { eventBus, EventNames, useEventListener } from '../utils/eventBus';
import logger from '../utils/simpleLogger';

// Extracted components
import { ChatPanelHeader } from './chat/ChatPanelHeader';
import { EmptyStateView } from './chat/EmptyStateView';
import MessageList from './chat/MessageList';
import { ErrorNotification, SuccessNotification } from './chat/ChatNotifications';

// Custom hooks
import { useMessageExpansion } from './chat/hooks/useMessageExpansion';
import { useSuggestedContent } from './chat/hooks/useSuggestedContent';
import { useMessageActions } from './chat/hooks/useMessageActions';
import { useAutoScroll } from './chat/hooks/useAutoScroll';
import { useCitationComponents } from './chat/hooks/useCitationComponents';

// Types - Re-export for backwards compatibility
import type { ChatMessage, CitationData, ChatPanelProps } from './chat/types/chat.types';
export type { ChatMessage, CitationData, ChatPanelProps };

const ChatPanel: React.FC<ChatPanelProps> = ({
  conversation,
  selectedDocuments,
  selectedMinimalDocuments = [],
  availableDocuments = [],
  onDocumentSelect,
  messages,
  onMessagesUpdate,
  onCitationClick,
  onChunkClick,
  onStudioResultOpen,
  onMessageSave,
  onConversationCleared,
  successMessage: externalSuccessMessage,
  onDismissSuccess,
  // Lifted from useChatSettings (now in MainLayout)
  suggestedQuestionsEnabled = true,
  suggestedTasksEnabled = true,
  configInfo,
  historicalConfigInfo = {},
  loadingConfigInfo = {},
  onModelChange: handleModelChange,
  onTemperatureChange: handleTemperatureChange,
  onSuggestedQuestionsToggle: handleSuggestedQuestionsToggle,
  onSuggestedTasksToggle: handleSuggestedTasksToggle,
  onConversationRename,
  onConversationPin,
  onConversationArchive,
  onConversationCopy,
  isSidebarOpen,
  onToggleSidebar,
  onNewConversation,
}) => {
  const { theme } = useTheme();
  // Typing state bridged from ChatInputArea via EventBus
  const [isTyping, setIsTyping] = useState(false);
  useEventListener(EventNames.TYPING_STATE_CHANGED, (data) => {
    setIsTyping(data?.isTyping ?? false);
  });
  useGlobalLoading(); // keep context alive but don't render LoadingIndicator
  const isAdmin = apiService.getCurrentUser()?.is_admin || false;

  // Processing status from SSE events (Buscando docs... / Generando...)
  const [processingStatus, setProcessingStatus] = useState<string | null>(null);
  const [processingCode, setProcessingCode] = useState<string | null>(null);
  useEventListener(EventNames.PROCESSING_STATUS_CHANGED, (data) => {
    setProcessingStatus(data?.text || null);
    setProcessingCode(data?.code || null);
  });
  useEffect(() => {
    if (!isTyping) {
      setProcessingStatus(null);
      setProcessingCode(null);
    }
  }, [isTyping]);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  // Hooks
  const {
    successMessage: internalSuccessMessage,
    error,
    deleteMessage,
    copyMessage,
    saveMessage,
    editMessage,
    rateMessage,
    regenerateMessage,
  } = useMessageActions({ messages, onMessagesUpdate, onMessageSave });
  const successMessage = externalSuccessMessage ?? internalSuccessMessage;

  const {
    expandedReferences,
    expandedContext,
    expandedUsageStats,
    toggleExpanded: toggleMessageExpansion,
  } = useMessageExpansion();

  const {
    suggestedQuestions,
    isLoadingQuestions: isLoadingSuggestions,
    showQuestions: showSuggestedQuestions,
    questionsQuality: suggestionsQuality,
    generationMetadata,
    suggestedTasks,
    isLoadingTasks,
    showTasks: showSuggestedTasks,
  } = useSuggestedContent({
    messages,
    selectedMinimalDocuments,
    conversationId: conversation?.id,
    isTyping,
    questionsEnabled: suggestedQuestionsEnabled,
    tasksEnabled: suggestedTasksEnabled,
  });

  useAutoScroll({ messages, messagesContainerRef, messagesEndRef, lastMessageRef });

  // Scroll-to-bottom button state
  // Checks both user scroll events AND content growth (ResizeObserver)
  // so the button appears during streaming even if the user hasn't scrolled.
  const [isNearBottom, setIsNearBottom] = useState(true);
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const checkBottom = () => {
      setIsNearBottom(container.scrollHeight - container.scrollTop - container.clientHeight < 150);
    };
    container.addEventListener('scroll', checkBottom, { passive: true });
    // Also detect content growth (streaming adds text without scroll events)
    const ro = new ResizeObserver(checkBottom);
    // Observe the inner content wrapper (first child), not the scroll container
    if (container.firstElementChild) ro.observe(container.firstElementChild);
    return () => {
      container.removeEventListener('scroll', checkBottom);
      ro.disconnect();
    };
  }, []);
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Handlers
  const handleOpenPDF = useCallback(
    (citationData: CitationData) => onCitationClick?.(citationData),
    [onCitationClick]
  );
  const handleOpenChunk = useCallback(
    (citationData: CitationData) => onChunkClick?.(citationData),
    [onChunkClick]
  );
  const { createCitationComponents } = useCitationComponents({
    onOpenPDF: handleOpenPDF,
    onOpenChunk: handleOpenChunk,
  });

  const handleSuggestedQuestionClick = useCallback((question: string) => {
    eventBus.emit(EventNames.SUGGESTED_QUESTION_CLICKED, { question, autoSend: true });
    logger.debug(`Pregunta seleccionada: "${question}"`);
  }, []);

  const handleSuggestedTaskClick = useCallback((task: { id: string; name: string }) => {
    eventBus.emit(EventNames.SUGGESTED_QUESTION_CLICKED, {
      question: task.name,
      autoSend: true,
      studio_id: task.id,
    });
    logger.debug(`Studio seleccionado: "${task.name}" (id: ${task.id})`);
  }, []);

  const handleDocumentsChange = useCallback(
    (documentIds: string[]) => onDocumentSelect?.(documentIds),
    [onDocumentSelect]
  );

  const suggestedProps = {
    showSuggestedQuestions,
    suggestedQuestions,
    isLoadingSuggestions,
    suggestionsQuality,
    generationMetadata,
    onSuggestedQuestionClick: handleSuggestedQuestionClick,
    showSuggestedTasks,
    suggestedTasks,
    isLoadingTasks,
    onSuggestedTaskClick: handleSuggestedTaskClick,
  };

  return (
    <div className={`h-full w-full flex flex-col ${theme.colors.bg.primary}`}>
      <ChatPanelHeader
        conversationName={conversation?.name}
        isPinned={conversation?.is_pinned}
        onRename={onConversationRename}
        onPin={onConversationPin}
        onArchive={onConversationArchive}
        onCopy={onConversationCopy}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={onToggleSidebar}
        onNewConversation={onNewConversation}
      />
      {error && <ErrorNotification message={error} />}

      {/* Messages content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 relative">
        {successMessage && (
          <SuccessNotification message={successMessage} onDismiss={onDismissSuccess} />
        )}

        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto scrollbar-thin py-6"
          style={{
            paddingLeft: 'clamp(1rem, 4vw, 2.5rem)',
            paddingRight: 'clamp(1rem, 4vw, 2.5rem)',
            boxSizing: 'border-box',
            overflowAnchor: 'auto',
          }}
        >
          <div
            className="mx-auto space-y-4"
            style={{ width: '100%', maxWidth: '95%', boxSizing: 'border-box' }}
          >
            {messages.length === 0 ? (
              <EmptyStateView
                selectedMinimalDocuments={selectedMinimalDocuments}
                {...suggestedProps}
              />
            ) : (
              <>
                <MessageList
                  messages={messages}
                  isTyping={isTyping}
                  processingStatus={processingStatus}
                  processingCode={processingCode}
                  lastMessageRef={lastMessageRef}
                  onCopy={copyMessage}
                  onSave={saveMessage}
                  onEdit={editMessage}
                  onDelete={deleteMessage}
                  onRate={rateMessage}
                  onRegenerate={regenerateMessage}
                  onCitationClick={handleOpenPDF}
                  onChunkClick={handleOpenChunk}
                  onStudioResultOpen={onStudioResultOpen}
                  expandedReferences={expandedReferences}
                  expandedContext={expandedContext}
                  expandedUsageStats={expandedUsageStats}
                  onToggleExpansion={toggleMessageExpansion}
                  isAdmin={isAdmin}
                  availableDocuments={availableDocuments}
                  selectedMinimalDocuments={selectedMinimalDocuments}
                  createCitationComponents={createCitationComponents}
                  {...suggestedProps}
                />
                {/* LoadingIndicator removed — TypingIndicator in MessageList handles processing status via SSE */}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
        </div>

        {/* Scroll-to-bottom button */}
        {!isNearBottom && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10
                       w-9 h-9 rounded-full bg-nb-surface border border-nb-border
                       shadow-lg flex items-center justify-center
                       hover:bg-nb-surface-hover hover:scale-110 active:scale-95 transition-all
                       animate-fade-in"
            aria-label="Ir al final"
          >
            <ChevronDown className="w-4 h-4 text-nb-text-secondary" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatPanel;
